import classNames from 'classnames';
import { useState, type ReactNode } from 'react';
import styled, { keyframes, type CSSProperties } from 'styled-components';

import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge';
import { useTimeoutCallback } from '@sorare/core/src/hooks/animations/useTimeoutCallback';
import {
  ensureHexColor,
  getRgbColorFromHex,
} from '@sorare/core/src/lib/colors';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

const shimmer = keyframes`
  0% {
    transform: translateX(-75%);
  }
  100% {
    transform: translateX(75%);
  }
`;
const Root = styled.div`
  position: relative;
  padding: var(--double-unit);
  border-radius: var(--double-unit) 0 0 var(--double-unit);
  background:
    conic-gradient(
        from 90deg at 6px 6px,
        rgba(var(--background-overlay-color), 0.1) 25%,
        var(--c-black) 0
      )
      0 0/8px 8px,
    conic-gradient(from 90deg at 6px 6px, var(--c-nd-50) 25%, var(--c-black) 0)
      0 0/8px 8px,
    var(--c-black);

  @media ${laptopAndAbove} {
    border-radius: var(--double-unit);
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    opacity: 0;
    transition: opacity 1s;
    box-shadow: 0 0 32px 4px rgba(var(--background-overlay-color), 0.5);
  }
  &.showHalo::before {
    opacity: 1;
  }
`;
const StyledNeonEdge = styled(NeonEdge)`
  position: absolute;
  inset: 0;
  overflow: hidden;
  border-radius: inherit;
`;
const Shimmer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  border-radius: inherit;
  overflow: hidden;

  &::before {
    content: '';
    z-index: 0;
    display: block;
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    transform: translateX(-75%);
    background: linear-gradient(
      -45deg,
      transparent 30%,
      var(--c-nd-300) 50%,
      transparent 70%
    );
    transform-origin: center;
    animation:
      ${shimmer} 0.8s,
      var(--ease-in-out-cubic);
    animation-delay: 1.2s;
  }
`;

type Props = {
  children: ReactNode;
  color: string | null;
  showHalo?: boolean;
  showShimmer?: boolean;
};
export const MissionToastBackground = ({
  children,
  color,
  showHalo,
  showShimmer,
}: Props) => {
  const hexColor = ensureHexColor(color);
  const [triggerHaloAnimation, setTriggerHaloAnimation] = useState(false);

  const timeoutCallback = useTimeoutCallback(() => {
    setTriggerHaloAnimation(true);
  }, 100);

  if (showHalo && !triggerHaloAnimation) {
    timeoutCallback();
  }

  return (
    <Root
      style={
        {
          '--background-overlay-color': getRgbColorFromHex(hexColor),
        } as CSSProperties
      }
      className={classNames({ showHalo: triggerHaloAnimation })}
    >
      {!!showShimmer && <Shimmer />}
      <StyledNeonEdge
        color={hexColor}
        side="left"
        showHalo="always"
        haloOpacity={0.6}
        spread="60%"
      />
      {children}
    </Root>
  );
};
